/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {map, Observable} from 'rxjs';

import {ApiConfig} from '../../config/api-config';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {ResourceHelper} from '../../../components/util/resource-helper';
import {OBJECT_TYPE, ObjectTypeFinder} from '../constants/object-type';
import {AdnApiClientService} from './adn-api-client.service';

export interface SearchApiParams {
  q?: string;
  types?: string | string[];
  labels?: string;
  blankOrderBy?: boolean;
  objectState?: string;
  [key: string]: any;
}

export interface SearchApiPage {
  searchResults?: any[];
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class SearchApiService {
  constructor(
    private readonly client: AdnApiClientService,
    private readonly localUserProfile: LocalUserProfile,
    private readonly apiConfig: ApiConfig
  ) {
  }

  search(params: SearchApiParams): Observable<SearchApiPage> {
    const requestParams = this.normalizeParams(params);
    const explicitObjectType = this.getExplicitObjectType(requestParams.types);
    return this.client.request<SearchApiPage>({
      method: 'GET',
      url: this.apiConfig.getVersionedUri('search'),
      params: requestParams
    }).pipe(map((data) => this.hydrateSearchResults(data, explicitObjectType)));
  }

  post(body: any, params: SearchApiParams = {}): Observable<SearchApiPage> {
    const requestParams = this.normalizeParams(params);
    const explicitObjectType = this.getExplicitObjectType(requestParams.types);
    return this.client.request<SearchApiPage>({
      method: 'POST',
      url: this.apiConfig.getVersionedUri('search'),
      params: requestParams,
      body
    }).pipe(map((data) => this.hydrateSearchResults(data, explicitObjectType)));
  }

  private normalizeParams(params: SearchApiParams): SearchApiParams {
    const normalized: SearchApiParams = {...params};
    if (Array.isArray(normalized.types)) {
      normalized.types = normalized.types.join(';');
    }
    if (!normalized.q && normalized.types && !normalized.blankOrderBy) {
      normalized.orderBy = 'NAME';
    }
    normalized.q = ResourceHelper.uiSrefUpdater(normalized.q);
    normalized.labels = ResourceHelper.uiSrefUpdater(normalized.labels);
    if (normalized.q && normalized.q.length < 3 && !normalized.q.endsWith('*')) {
      normalized.q += '*';
    }
    if (!normalized.objectState) {
      normalized.objectState = 'ACTIVE';
    }
    return normalized;
  }

  private hydrateSearchResults(data: SearchApiPage, explicitObjectType: any): SearchApiPage {
    if (!data?.searchResults) {
      return data;
    }
    data.searchResults = data.searchResults.flatMap((result: any) => {
      if (this.localUserProfile.get().app === 'DATA' && result.type === 'UserSegment') {
        result.type = 'Segment';
        result.objectType = OBJECT_TYPE.Segment;
        return [result];
      }
      const objectType = (result.type ? ObjectTypeFinder.findSearchObject(result.type) : null) || explicitObjectType;
      if (!objectType) {
        return [];
      }
      if (!result.type) {
        result.type = objectType.type;
      }
      result.objectType = objectType;
      return [result];
    });
    return data;
  }

  private getExplicitObjectType(types: string | string[] | undefined): any {
    if (!types) {
      return null;
    }
    const typeList = Array.isArray(types) ? types : String(types).split(';');
    if (typeList.length !== 1 || !typeList[0]) {
      return null;
    }
    return ObjectTypeFinder.findSearchObject(typeList[0]) || null;
  }
}
